import { BoMItemRow, PartTypeRow } from 'model/Project/BoMItemRow'

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { cloneDeep } from 'lodash'
import { MaterialSelectorDtoWithTranslations } from 'model/materials/MaterialSelectorDtoWithTranslation'
import {
  BomItemPointer,
  MaterialHeaderPointer,
} from 'model/Project/BomItemPointer'
import { ArticleSelectorUI } from '../../model/ArticleSelectorUI'
import {
  BoMItemActivityArticleDto,
  BoMItemActivityDto,
  KeywordDto,
  MaterialSummaryDto,
  WorkingStepType,
} from '../../services/APIs/InternalAPI/internal-api.contracts'

export type ActivitiesClipboard = {
  baseBomItemPointer: BomItemPointer
  activities: BoMItemActivityDto[]
}

export type MaterialState = {
  readonly activeRows: PartTypeRow[]
  readonly articleSelectorUI: ArticleSelectorUI
  readonly isMaterialSelectorModalOpen: boolean
  activitiesClipboard: ActivitiesClipboard
  readonly wasChanged: boolean
  readonly materialHeaderPointer: MaterialHeaderPointer
  readonly isLoading: boolean
  remnantStrategyModal: {
    open: boolean
    materialId: string
    resourceId: string
    projectId: string
  }
  readonly workingStepType: WorkingStepType
}

const initialState: MaterialState = {
  activeRows: [],
  articleSelectorUI: undefined,
  activitiesClipboard: undefined,
  isMaterialSelectorModalOpen: false,
  wasChanged: false,
  materialHeaderPointer: undefined,
  isLoading: false,
  remnantStrategyModal: {
    open: false,
    materialId: '',
    resourceId: '',
    projectId: '',
  },
  workingStepType: undefined,
}

const materialsSlice = createSlice({
  initialState: initialState,
  name: 'material',
  reducers: {
    openMaterialModal: (
      state,
      action: PayloadAction<{
        activeRows: PartTypeRow[]
        materialHeaderPointer: MaterialHeaderPointer
        workingStepType: WorkingStepType
      }>,
    ) => {
      state.activeRows = action.payload.activeRows
      state.materialHeaderPointer = action.payload.materialHeaderPointer
      state.isLoading = true
      state.isMaterialSelectorModalOpen = true
      state.workingStepType = action.payload.workingStepType
    },
    closeWithoutSave: () => {
      return initialState
    },
    setPossibleTokensAndMaterials: (
      state,
      action: PayloadAction<{
        tokensAndMaterials: MaterialSelectorDtoWithTranslations
        selectedMaterial?: BoMItemActivityArticleDto
        workingStepType?: WorkingStepType
      }>,
    ) => {
      const materialSelectorUI = new ArticleSelectorUI(
        action.payload.tokensAndMaterials,
        state.articleSelectorUI?.selectedMaterial ||
          action.payload.selectedMaterial,
        action.payload.workingStepType,
      )

      state.articleSelectorUI = materialSelectorUI
      state.isLoading = false
    },
    setTokens: (state, action: PayloadAction<Record<string, KeywordDto[]>>) => {
      const newMaterialSummary: MaterialSummaryDto = {
        id: undefined,
        tokens: action.payload,
      }

      const newRows = state.activeRows.map((x) => {
        return Object.assign({}, x, {
          materialSummary: newMaterialSummary,
        })
      })

      newRows.forEach((row) => {
        row.materialSummary.id = undefined
      })

      state.articleSelectorUI = {
        ...state.articleSelectorUI,
        selectedMaterial: newMaterialSummary,
        selectedTokens: Object.values(newMaterialSummary.tokens).flat(),
        selectableTokens: undefined,
      }

      state.activeRows = newRows
    },
    setMaterial: (state, action: PayloadAction<MaterialSummaryDto>) => {
      const newMaterialSummary = {
        ...action.payload,
      }

      const newRows = state.activeRows.map((x) => {
        return {
          ...x,
          materialSummary: newMaterialSummary,
        }
      })

      const newUI = Object.assign({}, state.articleSelectorUI, {
        selectedMaterial: newMaterialSummary,
        selectedTokens: Object.values(action?.payload?.tokens || {}).flat(),
      })

      state.articleSelectorUI = newUI
      state.activeRows = newRows
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    set3dModelPath: (state, action: PayloadAction<string>) => {
      const newRow: BoMItemRow = Object.assign({}, state.activeRows[0], {
        threeDModelPath: action.payload,
      })

      state.activeRows = [newRow]
    },
    copyActivityToClipboard: (
      state,
      action: PayloadAction<{
        baseBomItemPointer: BomItemPointer
        activities: BoMItemActivityDto[]
      }>,
    ) => {
      const clipboardActivities = []

      action.payload.activities.forEach((activity) => {
        const _keywords = cloneDeep(activity.keywords)

        delete _keywords['dimensions']
        delete _keywords['shape']

        const clipboardActivity = {
          ...activity,
          keywords: _keywords,
        }

        clipboardActivities.push(clipboardActivity)
      })

      state.activitiesClipboard = {
        baseBomItemPointer: action.payload.baseBomItemPointer,
        activities: [...clipboardActivities],
      }
    },
    openModalRemnantStrategy: (
      state,
      action: PayloadAction<{
        materialId: string
        resourceId: string
        projectId: string
      }>,
    ) => {
      state.remnantStrategyModal.open = true
      state.remnantStrategyModal.materialId = action.payload.materialId
      state.remnantStrategyModal.resourceId = action.payload.resourceId
      state.remnantStrategyModal.projectId = action.payload.projectId
    },
    closeRemnantStrategyModal: (state) => {
      state.remnantStrategyModal = {
        open: false,
        materialId: '',
        resourceId: '',
        projectId: '',
      }
    },
    clipboardClear: (state) => {
      state.activitiesClipboard = undefined
    },
  },
})

export default materialsSlice.reducer

export const materialActions = materialsSlice.actions
